import {
  clearToggleTextKeysEnabledUserIdCookie,
  getToggleTextKeysUserIdFromCookie,
  LITHIUM_TEXT_TOGGLE_COOKIE
} from '@aurora/shared-apollo/helpers/ToggleTextKeysHelper';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import ToggleTextKeysContext from '@aurora/shared-client/components/context/ToggleTextKeysContext/ToggleTextKeysContext';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import { EndUserPages } from '@aurora/shared-types/pages/enums';
import React, { useContext, useState, useCallback } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';
import { TOGGLE_TEXT_KEYS_FLOATING_BOX_ID } from '../ToggleTextKeysFloatingBox/ToggleTextKeysFloatingBox';
import Cookies from 'universal-cookie';
import useFloatingBox from '@aurora/shared-client/components/context/FloatingBoxContext/useFloatingBox';
import dynamic from 'next/dynamic';

const ToggleTextKeysFloatingBox = dynamic(
  () => import('../ToggleTextKeysFloatingBox/ToggleTextKeysFloatingBox'),
  { ssr: false }
);

/**
 * Set that contains the list of EndUserPages where the toggle box should NOT be displayed.
 */
const BYPASS_TOGGLE_TEXT_KEYS_PAGES = new Set([
  EndUserPages.ThemeEditorPage,
  EndUserPages.PageEditorPage
]);

const cookies = new Cookies();

/**
 * Context provider to toggle text keys.
 *
 * @author Agustin Sosa
 */
const ToggleTextKeysContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { authUser } = useContext(AppContext);
  const { addBox, removeBox, floatingBox, showBox } = useFloatingBox(
    TOGGLE_TEXT_KEYS_FLOATING_BOX_ID
  );
  const { router } = useEndUserRoutes();
  const [showTextKeys, setShowTextKeys] = useState<boolean>(false);

  /**
   * Hides Toggle box and removes cookie.
   */
  const onCloseToggleBox = useCallback(() => {
    clearToggleTextKeysEnabledUserIdCookie();
    setShowTextKeys(false);
    removeBox();
  }, [removeBox]);

  useIsomorphicLayoutEffect(() => {
    // Keep track if cookie is added or deleted.
    const cookieChangeListener = ({ name }) => {
      if (name === LITHIUM_TEXT_TOGGLE_COOKIE) {
        const userId = getToggleTextKeysUserIdFromCookie();
        const shouldShow = userId === authUser?.uid;
        setShowTextKeys(shouldShow);

        if (shouldShow) {
          addBox();
        } else {
          removeBox();
        }
      }
    };
    cookies.addChangeListener(cookieChangeListener);
    return () => {
      cookies.removeChangeListener(cookieChangeListener);
    };
  }, [authUser?.uid, addBox, removeBox]);

  useIsomorphicLayoutEffect(() => {
    // On page navigation, check if toggle box should be displayed based on authUser and if page is bypassed.
    const userId = getToggleTextKeysUserIdFromCookie();
    const newPageId = router.getCurrentPageName();
    const shouldBypass = BYPASS_TOGGLE_TEXT_KEYS_PAGES.has(newPageId);

    if (shouldBypass || userId !== authUser?.uid) {
      setShowTextKeys(false);
      removeBox();
    } else if (userId === authUser?.uid) {
      setShowTextKeys(true);
      addBox();
    }
  }, [authUser?.uid, router, router.path, addBox, removeBox]);

  return (
    <ToggleTextKeysContext.Provider value={{ showTextKeys: showTextKeys }}>
      {children}
      {showBox &&
        floatingBox(
          <ToggleTextKeysFloatingBox
            onCloseToggleTextKeys={onCloseToggleBox}
            onShowTextKeysToggle={() => setShowTextKeys(!showTextKeys)}
            showTextKeys={showTextKeys}
          />
        )}
    </ToggleTextKeysContext.Provider>
  );
};

export default ToggleTextKeysContextProvider;
